import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Input, Row, Select, Space, Spin, Tooltip, Typography } from 'antd';
import StandardTable from '../../../components/common/StandardTable';
import { api, fetchSqlData } from '../../../services/api';
import type { DccIndexTableMhiDoc } from '../../../types';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

const { Text } = Typography;

interface MhiDocsIndexTableProps {
  selectedFolder: string | null;
}

const MhiDocsIndexTable: React.FC<MhiDocsIndexTableProps> = ({ selectedFolder }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<DccIndexTableMhiDoc[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [fileIndexStatus, setFileIndexStatus] = useState<any | null>(null);

  const [transmittalFilter, setTransmittalFilter] = useState('');
  const [documentFilter, setDocumentFilter] = useState('');
  const [titleFilter, setTitleFilter] = useState('');
  const [titleMode, setTitleMode] = useState<'all' | 'nonblank' | 'blank'>('all');
  const [revisionFilter, setRevisionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<DccIndexTableMhiDoc[]>([]);

  const norm = (s: any) => String(s ?? '').toLowerCase();
  const splitTerms = (q: any) =>
    String(q ?? '')
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .filter(Boolean);
  const matchesAllTerms = (value: any, query: any) => {
    const terms = splitTerms(query);
    if (!terms.length) return true;
    const haystack = norm(value);
    return terms.every((t) => haystack.includes(t));
  };

  const isBlankTitle = (v: any) => {
    const s = String(v ?? '').trim();
    return !s;
  };

  useEffect(() => {
    if (!selectedFolder) {
      setRows([]);
      setError(null);
      setFileIndexStatus(null);
      setSelectedRowKeys([]);
      setSelectedRows([]);
      return;
    }
    const f = String(selectedFolder).replace(/\\/g, '/').replace(/^\/+/, '');
    if (!f || f.split('/')[0].toLowerCase() !== 'mhidocmails') {
      setRows([]);
      setError(null);
      setFileIndexStatus(null);
      setSelectedRowKeys([]);
      setSelectedRows([]);
      return;
    }

    const rootKey = (f.split('/')[0] || '').toUpperCase();

    const loadStatus = async () => {
      try {
        const resp = await api.get('/dcc/index/status', { params: { root: rootKey } });
        setFileIndexStatus(resp?.data?.status ?? null);
      } catch {
        setFileIndexStatus(null);
      }
    };

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchSqlData('dcc_indextable_mhidocs');
        const arr: DccIndexTableMhiDoc[] = Array.isArray(data) ? data : data?.data || [];
        setRows(arr || []);
      } catch (e: any) {
        setError(e?.message || 'Failed to load index table rows.');
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    loadStatus();
    load();
  }, [selectedFolder]);

  const filteredRows = useMemo(() => {
    let out = rows || [];
    const baseFolder = String(selectedFolder || '').replace(/\\/g, '/').replace(/^\/+/, '');
    if (baseFolder) {
      out = out.filter((r) => {
        const fp = String(r.folder_path || '').replace(/\\/g, '/').replace(/^\/+/, '');
        return fp.toLowerCase().startsWith(baseFolder.toLowerCase());
      });
    }
    if (transmittalFilter) {
      out = out.filter((r) => matchesAllTerms(r.transmittal_no, transmittalFilter));
    }
    if (documentFilter) {
      out = out.filter((r) => matchesAllTerms(r.document_no, documentFilter));
    }
    if (titleMode === 'blank') {
      out = out.filter((r) => isBlankTitle(r.title));
    }
    if (titleMode === 'nonblank') {
      out = out.filter((r) => !isBlankTitle(r.title));
    }
    if (titleFilter) {
      out = out.filter((r) => matchesAllTerms(r.title, titleFilter));
    }
    if (revisionFilter) {
      out = out.filter((r) => matchesAllTerms(r.revision, revisionFilter));
    }
    if (statusFilter) {
      out = out.filter((r) => String(r.status || '').toLowerCase() === statusFilter.toLowerCase());
    }
    return out;
  }, [rows, selectedFolder, transmittalFilter, documentFilter, titleMode, titleFilter, revisionFilter, statusFilter]);

  const selectionPayload = useMemo(() => {
    return (selectedRows || []).map((r) => ({
      transmittal_no: r.transmittal_no,
      document_no: r.document_no,
      revision: r.revision,
      folder_path: r.folder_path,
    }));
  }, [selectedRows]);

  const statusOptions = useMemo(() => {
    const set = new Set<string>();
    let out = rows || [];
    const baseFolder = String(selectedFolder || '').replace(/\\/g, '/').replace(/^\/+/, '');
    if (baseFolder) {
      out = out.filter((r) => {
        const fp = String(r.folder_path || '').replace(/\\/g, '/').replace(/^\/+/, '');
        return fp.toLowerCase().startsWith(baseFolder.toLowerCase());
      });
    }
    if (transmittalFilter) {
      out = out.filter((r) => matchesAllTerms(r.transmittal_no, transmittalFilter));
    }
    if (documentFilter) {
      out = out.filter((r) => matchesAllTerms(r.document_no, documentFilter));
    }
    if (titleMode === 'blank') {
      out = out.filter((r) => isBlankTitle(r.title));
    }
    if (titleMode === 'nonblank') {
      out = out.filter((r) => !isBlankTitle(r.title));
    }
    if (titleFilter) {
      out = out.filter((r) => matchesAllTerms(r.title, titleFilter));
    }
    if (revisionFilter) {
      out = out.filter((r) => matchesAllTerms(r.revision, revisionFilter));
    }

    out.forEach((r) => {
      if (r.status) set.add(String(r.status));
    });
    return Array.from(set).sort().map((s) => ({ value: s, label: s }));
  }, [rows, selectedFolder, transmittalFilter, documentFilter, titleMode, titleFilter, revisionFilter]);

  const resetFilters = () => {
    setTransmittalFilter('');
    setDocumentFilter('');
    setTitleFilter('');
    setTitleMode('all');
    setRevisionFilter('');
    setStatusFilter(undefined);
  };

  const reload = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchSqlData('dcc_indextable_mhidocs');
      const arr: DccIndexTableMhiDoc[] = Array.isArray(data) ? data : data?.data || [];
      setRows(arr || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load index table rows.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const reloadFileIndexStatus = async () => {
    const f0 = String(selectedFolder || '').replace(/\\/g, '/').replace(/^\/+/, '');
    if (!f0) return;
    const rootKey = (f0.split('/')[0] || '').toUpperCase();
    if (!rootKey) return;
    try {
      const resp = await api.get('/dcc/index/status', { params: { root: rootKey } });
      setFileIndexStatus(resp?.data?.status ?? null);
    } catch {
      setFileIndexStatus(null);
    }
  };

  const handleRefreshIndex = async () => {
    try {
      const f = String(selectedFolder || '').replace(/\\/g, '/').replace(/^\/+/, '');
      if (!f || f.split('/')[0].toLowerCase() !== 'mhidocmails') return;
      setRefreshing(true);
      await api.post(
        '/dcc/index-tables/mhidocs/refresh',
        {
          folder_prefix: f,
          limit: 200,
        },
        { headers: { 'Content-Type': 'application/json' } },
      );
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Refresh Index failed.';
      setError(msg);
    } finally {
      setRefreshing(false);
      await reload();
    }
  };

  const columns: any[] = [
    { title: 'Trans #', dataIndex: 'transmittal_no', key: 'transmittal_no', width: 90 },
    {
      title: 'Trans Date',
      dataIndex: 'transmittal_date',
      key: 'transmittal_date',
      width: 70,
      render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD') : ''),
    },
    { title: 'Document #', dataIndex: 'document_no', key: 'document_no', width: 120},
    { title: 'Rev', dataIndex: 'revision', key: 'revision', width: 45 },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 440,
      render: (v: any) => {
        const s = String(v ?? '');
        if (!s) return '';
        return (
          <Tooltip title={s} placement="topLeft">
            <span>{s}</span>
          </Tooltip>
        );
      },
    },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 50 },
    {
      title: 'Path',
      dataIndex: 'folder_path',
      key: 'folder_path',
      width: 100,
      render: (v: any) => {
        const s = String(v ?? '');
        if (!s) return '';
        return (
          <Tooltip title={s} placement="topLeft">
            <span>{s}</span>
          </Tooltip>
        );
      },
    },
  ];

  const f = String(selectedFolder || '').replace(/\\/g, '/');
  const isMhiDocs = f && f.replace(/^\/+/, '').split('/')[0].toLowerCase() === 'mhidocmails';
  if (!isMhiDocs) return null;

  return (
    <Card style={{ width: '100%', maxWidth: '100%' }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Row gutter={[8, 8]} align="middle">
          <Col xs={24} md={16}>
            <Space size={8} wrap>
              <Text type="secondary">File index status (dcc_search_index):</Text>
              {fileIndexStatus?.last_refresh_at ? (
                <Text code>
                  {dayjs(fileIndexStatus.last_refresh_at).format('YYYY-MM-DD HH:mm')}
                </Text>
              ) : (
                <Text type="secondary">n/a</Text>
              )}
              {typeof fileIndexStatus?.last_refresh_count === 'number' && (
                <Text type="secondary">rows: {fileIndexStatus.last_refresh_count}</Text>
              )}
              {fileIndexStatus?.last_error && (
                <Text type="danger">error: {String(fileIndexStatus.last_error)}</Text>
              )}
            </Space>
          </Col>
        </Row>

        <Row gutter={[8, 8]} align="middle">
          <Col xs={24} md={12}>
            <Space size={8} wrap>
              <Text type="secondary">Selected:</Text>
              <Text code>{selectedRowKeys.length}</Text>
              <Button
                disabled={selectedRowKeys.length === 0}
                draggable={selectedRowKeys.length > 0}
                onDragStart={(ev) => {
                  try {
                    ev.dataTransfer.setData('application/json', JSON.stringify(selectionPayload));
                    ev.dataTransfer.setData('text/plain', JSON.stringify(selectionPayload));
                    ev.dataTransfer.effectAllowed = 'copy';
                  } catch {
                    // ignore
                  }
                }}
              >
                Drag selected
              </Button>
              <Button
                disabled={selectedRowKeys.length === 0}
                onClick={() => {
                  setSelectedRowKeys([]);
                  setSelectedRows([]);
                }}
              >
                Clear selection
              </Button>
            </Space>
          </Col>
        </Row>

        <Row gutter={[8, 8]}>
          <Col xs={24} sm={12} md={3} lg={3}>
            <Input
              placeholder="Transmittal #"
              value={transmittalFilter}
              onChange={(e) => setTransmittalFilter(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={3} lg={3}>
            <Input
              placeholder="Document #"
              value={documentFilter}
              onChange={(e) => setDocumentFilter(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={5} lg={5}>
            <Space.Compact style={{ width: '100%' }}>
              <Select
                value={titleMode}
                onChange={(v) => setTitleMode(v)}
                style={{ width: 100 }}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'nonblank', label: 'Has title' },
                  { value: 'blank', label: '(Blank)' },
                ]}
              />
              <Input
                placeholder="Title"
                value={titleFilter}
                onChange={(e) => setTitleFilter(e.target.value)}
                allowClear
                style={{ flex: 1, minWidth: 0 }}
              />
            </Space.Compact>
          </Col>
          <Col xs={24} sm={12} md={2} lg={2}>
            <Input
              placeholder="Revision"
              value={revisionFilter}
              onChange={(e) => setRevisionFilter(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={2} lg={2}>
            <Select
              placeholder="Status"
              allowClear
              value={statusFilter}
              onChange={(v) => setStatusFilter(v || undefined)}
              options={statusOptions}
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="label"
            />
          </Col>
          <Col xs={24} sm={12} md={4} lg={4}>
            <Space>
              <Button onClick={resetFilters}>Reset</Button>
              <Button type="primary" onClick={handleRefreshIndex} loading={refreshing}>
                Refresh Index Table
              </Button>
            </Space>
          </Col>
        </Row>

        {error && (
          <Alert type="error" message="Failed to load index table rows" description={error} showIcon />
        )}

        <Spin spinning={loading}>
          <StandardTable
            columns={columns}
            dataSource={filteredRows}
            rowKey={(r: any) => String(r.id)}
            rowSelection={{
              selectedRowKeys,
              onChange: (keys, selected) => {
                setSelectedRowKeys(keys);
                setSelectedRows(selected as DccIndexTableMhiDoc[]);
              },
            }}
            onRow={(record: any) => ({
              draggable: true,
              onDragStart: (ev: React.DragEvent) => {
                try {
                  const payload = {
                    transmittal_no: record?.transmittal_no,
                    document_no: record?.document_no,
                    revision: record?.revision,
                    folder_path: record?.folder_path,
                  };
                  ev.dataTransfer.setData('application/json', JSON.stringify(payload));
                  ev.dataTransfer.setData('text/plain', JSON.stringify(payload));
                  ev.dataTransfer.effectAllowed = 'copy';
                } catch {
                  // ignore
                }
              },
              onDoubleClick: () => {
                const folderPath = String(record?.folder_path || '').replace(/\\/g, '/').replace(/^\/+/, '');
                if (!folderPath) return;
                const qs = new URLSearchParams();
                qs.set('path', folderPath);
                qs.set('root', 'mhidocmails');
                const url = `/dcc/documents?${qs.toString()}`;
                window.open(url, '_blank', 'noopener,noreferrer');
              },
            })}
            size="small"
            style={{ width: '100%' }}
            scroll={{ x: 'max-content' }}
            pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
          />
        </Spin>
      </Space>
    </Card>
  );
};

export default MhiDocsIndexTable;
